import axios from "axios";
import type { AppThunk } from "@Store/index";
import { updateExamsData, updateExamsReadyStatus } from "./examsPageReducer";
import config from "@Config/index";
import { getMetaSuccess } from "@App/appReducer";

interface GetExamsPageDataParams {
  slug: string;
  url?: string;
  staticContext?: any;
  isDraft?: boolean;
}

// Main category slugs that should appear as tabs
const MAIN_CATEGORY_KEYWORDS = [
  "exam",
  "eligibility",
  "syllabus",
  "exam-pattern",
  "pattern",
  "preparation",
  "fees",
  "exam-fees",
  "exam-dates",
  "dates",
  "scores",
  "results",
  "score",
];

// Define the order of tabs
const TAB_ORDER = ["exam", "eligibility", "syllabus", "preparation", "fees", "dates", "scores"];

// Exam full names mapping
const EXAM_FULL_NAMES: Record<string, string> = {
  gmat: "Graduate Management Admission Test",
  gre: "Graduate Record Examination",
  ielts: "International English Language Testing System",
  toefl: "Test of English as a Foreign Language",
  sat: "Scholastic Assessment Test",
  pte: "Pearson Test of English",
  act: "American College Testing",
  duolingo: "Duolingo English Test",
};

const getExamsPageData =
  ({ slug, staticContext, isDraft = false }: GetExamsPageDataParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateExamsReadyStatus("request"));

      const statusFilter = isDraft ? "draft" : "published";
      const endpoint = `/api/exam-posts?where[slug][equals]=${slug}&where[_status][equals]=${statusFilter}&depth=2&limit=1`;

      // Fetch the exam post
      const response = await axios.get(`${config.CMS_API_URL}${endpoint}`);
      const examPost = response.data?.docs?.[0];

      if (!examPost) {
        console.error(`Exam post not found for slug: ${slug}`);
        if (staticContext) {
          staticContext.statusCode = 404;
        }
        dispatch(updateExamsReadyStatus("failure"));
        return;
      }

      // Fetch ALL posts for this exam to filter out main categories
      const examId = examPost.examName?.id || examPost.examName;
      let relatedPosts = [];
      let mainCategoryPostIds: number[] = [];
      let tabs: any[] = [];

      if (examId) {
        try {
          // First, fetch all posts for this exam to identify main category posts and tabs
          const allPostsResponse = await axios.get(
            `${config.CMS_API_URL}/api/exam-posts?where[examName][equals]=${examId}&where[_status][equals]=published&depth=1&limit=100&sort=ranking`,
          );
          const allPosts = allPostsResponse.data?.docs || [];

          // Build tabs from category posts - only include main category posts
          const categoryPosts = allPosts.filter((post: any) => post.isCategory === true);

          // Filter to only include main category tabs (must match exactly or be exam-slug + keyword)
          const mainCategoryPosts = categoryPosts.filter((post: any) => {
            const postSlug = post.slug?.toLowerCase() || "";
            const examSlug = examPost.examName?.slug?.toLowerCase() || "";

            // Check if slug matches the pattern: {exam}-{keyword} or compound patterns
            return MAIN_CATEGORY_KEYWORDS.some((keyword) => {
              const exactMatch = postSlug === `${examSlug}-${keyword}`;
              const compoundMatch =
                postSlug === `${examSlug}-syllabus-and-exam-pattern` ||
                postSlug === `${examSlug}-scores-and-results` ||
                postSlug === `${examSlug}-exam-fees` ||
                postSlug === `${examSlug}-exam-dates` ||
                postSlug === `${examSlug}-exam-pattern`;
              return exactMatch || compoundMatch;
            });
          });

          // Sort tabs based on TAB_ORDER
          tabs = mainCategoryPosts
            .map((post: any) => ({
              label: post.name,
              slug: post.slug,
              order: TAB_ORDER.findIndex((keyword) => {
                const postSlug = post.slug?.toLowerCase() || "";
                const examSlug = examPost.examName?.slug?.toLowerCase() || "";
                return (
                  postSlug === `${examSlug}-${keyword}` ||
                  postSlug === `${examSlug}-exam-${keyword}` ||
                  (keyword === "syllabus" && postSlug.includes("syllabus")) ||
                  (keyword === "scores" && postSlug.includes("scores"))
                );
              }),
            }))
            .sort((a: any, b: any) => {
              // If order is -1 (not found), put at end
              if (a.order === -1) return 1;
              if (b.order === -1) return -1;
              return a.order - b.order;
            })
            .map(({ label, slug: tabSlug }: any) => ({ label, slug: tabSlug }));

          // Identify main category posts that should be excluded from related articles
          mainCategoryPostIds = allPosts
            .filter((post: any) => {
              // First check: must be marked as a category post
              if (!post.isCategory) {
                return false;
              }

              const postSlug = post.slug?.toLowerCase() || "";
              const examSlug = examPost.examName?.slug?.toLowerCase() || "";

              // Check if this is a main category tab post
              const isMainCategoryTab = MAIN_CATEGORY_KEYWORDS.some((keyword) => {
                const exactMatch = postSlug === `${examSlug}-${keyword}`;
                const compoundMatch =
                  postSlug === `${examSlug}-syllabus-and-exam-pattern` ||
                  postSlug === `${examSlug}-scores-and-results` ||
                  postSlug === `${examSlug}-exam-fees` ||
                  postSlug === `${examSlug}-exam-dates` ||
                  postSlug === `${examSlug}-exam-pattern`;
                return exactMatch || compoundMatch;
              });

              return isMainCategoryTab;
            })
            .map((post: any) => post.id);

          // Use the allPosts we already fetched and filter client-side
          // Exclude: current post + main category tabs
          const excludeIds = [examPost.id, ...mainCategoryPostIds];
          relatedPosts = allPosts.filter((post: any) => !excludeIds.includes(post.id));
        } catch (error) {
          console.error("Error fetching related posts:", error);
        }
      }

      // Set meta data
      const meta: any = {};
      meta.title = examPost.metaTitle || examPost.name || "Exam Guide";
      meta.description = examPost.metaDescription || examPost.mainParagraph || "";
      meta.keywords = examPost.metaKeywords || [];
      meta.excludeAlternateLinks = true;

      dispatch(getMetaSuccess(meta));

      // Transform the exam post data to match expected structure
      const examSlug = examPost.examName?.slug?.toLowerCase() || "";
      const resourceBlogs = (examPost.resourceBlogs || []).map((blog: any) => ({
        id: blog.id,
        title: blog.name || blog.title,
        image: blog.thumbnailImage?.url || blog.mainImage?.url || "",
        link: `/exams/${examSlug}/${blog.slug}`,
      }));
      const transformedExamPost = {
        ...examPost,
        postBody: examPost.body,
        examLogo: examPost.examName?.mainImage?.url || examPost.examName?.secondImage?.url,
        examFullName: EXAM_FULL_NAMES[examSlug] || examPost.examName?.name,
        tabs,
        resourceBlogs,
      };

      dispatch(
        updateExamsData({
          data: transformedExamPost,
          slug,
          relatedPosts,
        }),
      );
    } catch (error) {
      console.error("Error fetching exam post data:", error);
      if (staticContext) {
        staticContext.statusCode = 500;
      }
      dispatch(updateExamsReadyStatus("failure"));
    }
  };

export default getExamsPageData;
